import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCenterDetails, updateCenter, fetchCountries, fetchGovernments, clearError, clearSuccess, clearLocationDetails } from '../../../redux/Slices/LocationsSlice';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';

const EditCenter = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    
    const { centerDetails, countries, governments, loading, updating, error, success } = useSelector((state) => state.locations);

    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [formData, setFormData] = useState({
        name_ar: '',
        name_en: '',
        government_id: ''
    });

    useEffect(() => {
        setTitle('تعديل مركز');
        dispatch(fetchCountries());
        dispatch(fetchCenterDetails(id));
        
        return () => {
            dispatch(clearLocationDetails());
        };
    }, [setTitle, dispatch, id]);

    // When center details load, populate form and figure out country based on government
    useEffect(() => {
        if (centerDetails && countries.length > 0) {
            setFormData({
                name_ar: centerDetails.name_ar || centerDetails.name || '',
                name_en: centerDetails.name_en || '',
                government_id: centerDetails.government_id || ''
            });

            // Find government from API to get the country, or fetch government details... 
            // Wait, we might not have all governments. The best UX is to load the government's info.
            // Since we don't have government details directly inside center details usually, 
            // we will fetch governments for ALL countries one by one or we might need an endpoint.
            // For now, if center Details doesn't include country_id, we just fetch governments for the first country,
            // or we expect the user to select the country again if they want to change government.
            // Let's assume centerDetails contains government_id. We fetch governments where country is known.
            // Actually, best is we leave Country unselected unless we know it.
            if (centerDetails.country_id) {
                setSelectedCountryId(centerDetails.country_id);
            }
        }
    }, [centerDetails, countries]);

    useEffect(() => {
        if (selectedCountryId) {
            dispatch(fetchGovernments(selectedCountryId));
        }
    }, [selectedCountryId, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
            navigate('/admin-locations/centers');
        }
    }, [error, success, dispatch, navigate]);

    const handleCountryChange = (e) => {
        setSelectedCountryId(e.target.value);
        setFormData(prev => ({ ...prev, government_id: '' })); // Reset gov when country changes
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!formData.name_ar || !formData.name_en || !formData.government_id) {
            showErrorToast('الرجاء تعبئة جميع الحقول المطلوبة');
            return;
        }

        const submitData = new FormData();
        submitData.append('name_ar', formData.name_ar);
        submitData.append('name_en', formData.name_en);
        submitData.append('government_id', formData.government_id);

        dispatch(updateCenter({ id, formData: submitData }));
    };

    if (loading && !centerDetails) {
        return (
            <div className="main_content text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل بيانات المركز...</p>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                <div className="card shadow-lg border-0">
                    <div className="card-header bg-white pb-0 border-0">
                        <h4 className="mb-0">تعديل مركز #{id}</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                {/* <div className="col-md-6">
                                    <label className="form-label">الدولة (لتغيير المحافظة) <span className="text-muted">(اختياري للفلترة)</span></label>
                                    <select
                                        className="form-select hover-shadow transition-all"
                                        value={selectedCountryId}
                                        onChange={handleCountryChange}
                                    >
                                        <option value="" disabled>-- اختر لتصفية المحافظات --</option>
                                        {countries && countries.map(country => (
                                            <option key={country.id} value={country.id}>
                                                {country.name_ar || country.name || country.name_en}
                                            </option>
                                        ))}
                                    </select>
                                </div> */}
                                <div className="col-md-6">
                                    <label className="form-label">المحافظة <span className="text-danger">*</span></label>
                                    {/* If country isn't selected, we might not have the correct governments list showing, but usually we just want them to change name */}
                                    <select
                                        className="form-select hover-shadow transition-all"
                                        name="government_id"
                                        value={formData.government_id}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {/* If we have the governments from the selected country, show them, otherwise just show current one if possible */}
                                        <option value="" disabled>-- اختر المحافظة --</option>
                                        {governments && governments.map(gov => (
                                            <option key={gov.id} value={gov.id}>
                                                {gov.name_ar || gov.name || gov.name_en}
                                            </option>
                                        ))}
                                        {!selectedCountryId && formData.government_id && (
                                            <option value={formData.government_id}>
                                                المحافظة الحالية (رقم {formData.government_id}) - اختر دولة لظهور المزيد
                                            </option>
                                        )}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">اسم المركز (بالعربية) <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control hover-shadow transition-all"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">اسم المركز (بالإنجليزية) <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className="form-control hover-shadow transition-all"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-4 d-flex gap-2">
                                <button type="submit" className="btn btn-primary px-4 py-2" disabled={updating || !formData.government_id}>
                                    {updating ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                                </button>
                                <button type="button" className="btn btn-secondary px-4 py-2" onClick={() => navigate('/admin-locations/centers')} disabled={updating}>
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCenter;
